import { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import InvestorPageShell from "../../../components/Investor/InvestorPageShell";
import KycBanner from "../../../components/Investor/KycBanner";
import InvestmentDetailHeroPremium, {
  InvestmentHeroSecondaryActions,
} from "../../../components/Investor/InvestmentDetail/InvestmentDetailHeroPremium";
import InvestmentQuickFacts from "../../../components/Investor/InvestmentDetail/InvestmentQuickFacts";
import InvestmentDetailPremiumContent from "../../../components/Investor/InvestmentDetail/InvestmentDetailPremiumContent";
import InvestmentDetailSidebarRail from "../../../components/Investor/InvestmentDetail/InvestmentDetailSidebarRail";
import { InvestmentDetailMobileBar } from "../../../components/Investor/InvestmentDetailSummary";
import InvestmentRequestModal from "../../../components/Investor/InvestmentRequestModal";
import {
  InvestmentDetailGrid,
  InvestmentDetailMainColumn,
  InvestmentDetailPageFrame,
  InvestmentDetailRailColumn,
  InvestmentDetailRailSticky,
} from "../../../components/Investor/layout/InvestorLayoutPrimitives";
import { enrichOpportunity } from "../../../data/investor/opportunityDetails";
import { useInvestmentInvestFlow } from "../../../hooks/useInvestmentInvestFlow";
import { useInvestorGate } from "../../../hooks/useInvestorGate";
import { useInvestorSession } from "../../../hooks/useInvestorSession";
import { getInvestorQueryParams, withInvestorQuery } from "../../../utils/investorRouteHelpers";
import {
  useGetInvestorOpportunityQuery,
  useGetInvestorSupportInfoQuery,
} from "../../../store/investor/investorInvestmentsApi";
import { useGetStaffOpportunityBySlugQuery } from "../../../store/admin/investorPortal/staffInvestmentsApi";
import { useGetSupportSettingsQuery } from "../../../store/admin/settings/supportSettingsSlice";
import {
  isInvestable,
  staffDetailToInvestorOpportunity,
} from "../../../utils/investorInvestmentsAdapter";
import { mapStaffOpportunityDetail } from "../../../utils/staffInvestmentsAdapter";
import TableSkeletonLoader from "../../../components/Common/TableSkeletonLoader";

function normalizeCompanySupport(source) {
  if (!source) return { phone: "", email: "", whatsapp: "" };
  const phone = source.phone || source.support_phone || "";
  const email = source.email || source.support_email || "";
  const whatsapp = source.whatsapp || phone;
  return { phone, email, whatsapp };
}

export default function InvestorInvestmentDetailPage() {
  const { opportunityId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isStaffPreview = searchParams.get("preview") === "staff";
  const query = getInvestorQueryParams(searchParams);
  const { isKycApproved, kycStatus } = useInvestorGate();
  const { profile, activeUserId } = useInvestorSession();
  const { startInvest } = useInvestmentInvestFlow(opportunityId);

  const { data: apiOpportunity, isFetching: isLoadingApi } = useGetInvestorOpportunityQuery(opportunityId, {
    skip: isStaffPreview,
  });
  const { data: staffPreview, isFetching: isLoadingPreview } = useGetStaffOpportunityBySlugQuery(opportunityId, {
    skip: !isStaffPreview,
  });
  const { data: investorSupport } = useGetInvestorSupportInfoQuery(undefined, {
    skip: isStaffPreview,
  });
  const { data: staffSupportSettings } = useGetSupportSettingsQuery(undefined, {
    skip: !isStaffPreview,
  });

  const companySupport = useMemo(() => {
    if (isStaffPreview) return normalizeCompanySupport(staffSupportSettings);
    return normalizeCompanySupport(investorSupport);
  }, [isStaffPreview, staffSupportSettings, investorSupport]);

  const opportunity = useMemo(() => {
    let base = null;
    if (isStaffPreview && staffPreview) {
      const mappedStaff = mapStaffOpportunityDetail(staffPreview);
      base = enrichOpportunity(staffDetailToInvestorOpportunity(mappedStaff));
    } else if (apiOpportunity) {
      base = enrichOpportunity(apiOpportunity);
    }
    if (!base) return null;
    // Company Support Settings — not per-opportunity contacts
    return { ...base, support: companySupport, supportContact: companySupport };
  }, [isStaffPreview, staffPreview, apiOpportunity, companySupport]);
  const [showInvestModal, setShowInvestModal] = useState(false);
  const [selectedUnits, setSelectedUnits] = useState(null);

  const detailPath = `/invest/investments/${opportunityId}`;
  const units = selectedUnits ?? opportunity?.minUnitsPurchase ?? 1;

  useEffect(() => {
    if (opportunity?.minUnitsPurchase) {
      setSelectedUnits((prev) => prev ?? opportunity.minUnitsPurchase);
    }
  }, [opportunity?.minUnitsPurchase]);

  useEffect(() => {
    if (query.intent !== "invest" || query.itemId !== opportunityId) return;
    if (!opportunity) return;

    if (!isInvestable(opportunity)) {
      navigate(detailPath, { replace: true });
      return;
    }

    if (!isKycApproved) {
      navigate(
        withInvestorQuery("/invest/profile", {
          redirect: detailPath,
          intent: query.intent,
          itemId: query.itemId,
          tab: "verification",
        }),
        { replace: true }
      );
      return;
    }

    setShowInvestModal(true);
    navigate(detailPath, { replace: true });
  }, [query.intent, query.itemId, opportunityId, opportunity, isKycApproved, navigate, detailPath]);

  if (isLoadingApi || isLoadingPreview) {
    return (
      <InvestorPageShell wide backTo="/invest/investments" backLabel="Back to investments">
        <TableSkeletonLoader rows={6} columns={2} />
      </InvestorPageShell>
    );
  }

  if (!opportunity) {
    return <Navigate to="/invest/investments" replace />;
  }

  const canInvest = isInvestable(opportunity);
  const unavailableNote =
    opportunity.investUnavailableReason ||
    "This opportunity isn’t open for investment yet.";

  const handleInvest = () => {
    startInvest(() => setShowInvestModal(true), opportunity);
  };

  return (
    <InvestorPageShell wide backTo="/invest/investments" backLabel="Back to investments">
      <InvestmentDetailPageFrame className="investment-detail-premium">
        {isStaffPreview || opportunity._staffPreview ? (
          <div
            className="alert mb-3 py-2 px-3 small"
            style={{ background: "rgba(49, 119, 140, 0.1)", border: "1px solid rgba(49, 119, 140, 0.35)", color: "#31778c" }}
            role="status"
          >
            Staff preview — live content from the Investment Hub. Investors see this after publish.
          </div>
        ) : null}
        <KycBanner
          status={kycStatus}
          redirectTo={detailPath}
          intent="invest"
          itemId={opportunityId}
        />

        {!canInvest ? (
          <div
            className="alert mb-3 py-2 px-3 small"
            style={{
              background: "rgba(255, 149, 0, 0.08)",
              border: "1px solid rgba(255, 149, 0, 0.3)",
              color: "#525252",
            }}
            role="status"
          >
            {unavailableNote}
          </div>
        ) : null}

        <InvestmentDetailGrid>
          <InvestmentDetailMainColumn>
            <InvestmentDetailHeroPremium
              opportunity={opportunity}
              onInvest={handleInvest}
              canInvest={canInvest}
              unavailableNote={unavailableNote}
            />
            <InvestmentHeroSecondaryActions
              onInvest={handleInvest}
              canInvest={canInvest}
              unavailableNote={unavailableNote}
            />
            <InvestmentQuickFacts opportunity={opportunity} />
            <InvestmentDetailPremiumContent opportunity={opportunity} />
          </InvestmentDetailMainColumn>

          <InvestmentDetailRailColumn aria-label="Investment actions and resources">
            <InvestmentDetailRailSticky>
              <InvestmentDetailSidebarRail
                opportunity={opportunity}
                onInvest={handleInvest}
                selectedUnits={units}
                onUnitsChange={setSelectedUnits}
                canInvest={canInvest}
                unavailableNote={unavailableNote}
              />
            </InvestmentDetailRailSticky>
          </InvestmentDetailRailColumn>
        </InvestmentDetailGrid>

        {canInvest ? (
          <InvestmentDetailMobileBar
            opportunity={opportunity}
            onInvest={handleInvest}
            selectedUnits={units}
          />
        ) : null}

        <InvestmentRequestModal
          show={showInvestModal && canInvest}
          onClose={() => setShowInvestModal(false)}
          opportunity={opportunity}
          profile={profile}
          activeUserId={activeUserId}
          defaultUnits={units}
        />
      </InvestmentDetailPageFrame>
    </InvestorPageShell>
  );
}
